import api from '../axiosConfig';
import { SETTINGS } from '../config/apiEndpoints';

export const SettingsService = {
    getBranding: async (tenantId) => {
        const headers = tenantId ? { 'x-tenant-id': String(tenantId) } : {};
        try {
            // First attempt /api/v1/settings/brand
            const response = await api.get(SETTINGS.BRAND, { headers });
            return response.data;
        } catch (brandError) {
            // Fallback to /api/v1/settings/branding if /settings/brand 404s
            if (brandError.response?.status === 404) {
                try {
                    const response = await api.get(SETTINGS.BRANDING, { headers });
                    return response.data;
                } catch (brandingError) {
                    console.error('SettingsService getBranding error:', brandingError);
                    throw brandingError;
                }
            }
            console.error('SettingsService getBranding error:', brandError);
            throw brandError;
        }
    },

    getPublicBranding: async (tenantId) => {
        try {
            const headers = tenantId ? { 'x-tenant-id': String(tenantId) } : {};
            const response = await api.get(SETTINGS.PUBLIC_BRANDING, { headers });
            return response.data;
        } catch (error) {
            console.warn('SettingsService getPublicBranding error:', error);
            throw error;
        }
    },

    updateBranding: async (brandingData, tenantId) => {
        const headers = tenantId ? { 'x-tenant-id': String(tenantId) } : {};
        try {
            // First attempt /api/v1/settings/brand
            const response = await api.patch(SETTINGS.BRAND, brandingData, { headers });
            return response.data;
        } catch (brandError) {
            if (brandError.response?.status === 404) {
                try {
                    const response = await api.patch(SETTINGS.BRANDING, brandingData, { headers });
                    return response.data;
                } catch (brandingError) {
                    console.error('SettingsService updateBranding error:', brandingError);
                    throw brandingError;
                }
            }
            console.error('SettingsService updateBranding error:', brandError);
            throw brandError;
        }
    },

    updatePolicies: async (policyData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.POLICIES, policyData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updatePolicies error:', error);
            throw error;
        }
    },

    getCancellationPolicies: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1; // Assuming backend is 1-indexed
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;

            const response = await api.get(SETTINGS.CANCELLATION_POLICIES, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            if (params.page !== undefined) return data;
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getCancellationPolicies error:', error);
            throw error;
        }
    },

    getCancellationReasons: async (tenantId) => {
        try {
            const response = await api.get(SETTINGS.CANCELLATION_REASONS, {
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getCancellationReasons error:', error);
            throw error;
        }
    },

    getGlobalDefaultCancellationPolicy: async (tenantId) => {
        try {
            const response = await api.get(SETTINGS.GLOBAL_DEFAULT_CANCELLATION_POLICY, {
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getGlobalDefaultCancellationPolicy error:', error);
            throw error;
        }
    },

    /**
     * Create a new cancellation policy
     * POST /api/v1/settings/cancellation-policies
     * @param {Object} policyData - { name, refundMethod, appliedTo }
     * @param {string} tenantId
     * @returns {Promise<Object>} Created policy
     */
    createCancellationPolicy: async (policyData, tenantId) => {
        try {
            const response = await api.post(SETTINGS.CANCELLATION_POLICIES, policyData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService createCancellationPolicy error:', error);
            throw error;
        }
    },

    /**
     * Update an existing cancellation policy
     * PATCH /api/v1/settings/cancellation-policies/{id}
     * @param {string} id - Policy ID
     * @param {Object} policyData - Payload (e.g. { name: "..." })
     * @param {string} tenantId
     */
    updateCancellationPolicy: async (id, policyData, tenantId) => {
        try {
            const response = await api.patch(`${SETTINGS.CANCELLATION_POLICIES}/${id}`, policyData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateCancellationPolicy error:', error);
            throw error;
        }
    },

    /**
     * Delete a cancellation policy
     * DELETE /api/v1/settings/cancellation-policies/{id}
     * @param {string} id - Policy ID
     * @param {string} tenantId
     */
    deleteCancellationPolicy: async (id, tenantId) => {
        try {
            const response = await api.delete(`${SETTINGS.CANCELLATION_POLICIES}/${id}`, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService deleteCancellationPolicy error:', error);
            throw error;
        }
    },

    /**
     * Configure rules for a cancellation policy
     * POST /api/v1/settings/cancellation-policies/{id}/rules
     * @param {string} id - Parent Policy ID
     * @param {Array} rulesData - Array of rule objects [{timeBeforeCheckIn, feeType, feeAmount}]
     * @param {string} tenantId 
     */
    updateCancellationPolicyRules: async (id, rulesData, tenantId) => {
        try {
            const response = await api.post(`${SETTINGS.CANCELLATION_POLICIES}/${id}/rules`, rulesData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateCancellationPolicyRules error:', error);
            throw error;
        }
    },

    /**
     * Update a single cancellation rule
     * PATCH /api/v1/settings/cancellation-rules/{id}
     * @param {string} id - Rule ID
     * @param {Object} ruleData - { timeBeforeCheckIn, feeType, feeAmount }
     * @param {string} tenantId 
     */
    updateCancellationRule: async (id, ruleData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.CANCELLATION_RULES(id), ruleData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateCancellationRule error:', error);
            throw error;
        }
    },

    /**
     * Delete a single cancellation rule
     * DELETE /api/v1/settings/cancellation-rules/{id}
     * @param {string} id - Rule ID
     * @param {string} tenantId 
     */
    deleteCancellationRule: async (id, tenantId) => {
        try {
            const response = await api.delete(SETTINGS.CANCELLATION_RULES(id), {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService deleteCancellationRule error:', error);
            throw error;
        }
    },

    updateLocalization: async (localizationData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.LOCALIZATION, localizationData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateLocalization error:', error);
            throw error;
        }
    },

    getTaxes: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else {
                axiosParams.noPagination = true;
            }
            
            const response = await api.get(SETTINGS.TAXES.BASE, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            
            if (params.page !== undefined) {
                return data;
            }
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getTaxes error:', error);
            throw error;
        }
    },

    createTax: async (taxData, tenantId) => {
        try {
            const response = await api.post(SETTINGS.TAXES.BASE, taxData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService createTax error:', error);
            throw error;
        }
    },

    updateTax: async (id, taxData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.TAXES.BY_ID(id), taxData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateTax error:', error);
            throw error;
        }
    },

    deleteTax: async (id, tenantId) => {
        try {
            const response = await api.delete(SETTINGS.TAXES.BY_ID(id), {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService deleteTax error:', error);
            throw error;
        }
    },

    getBookingSources: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else {
                axiosParams.noPagination = true;
            }
            
            const response = await api.get(SETTINGS.BOOKING_SOURCES.BASE, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            
            if (params.page !== undefined) {
                return data;
            }
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getBookingSources error:', error);
            throw error;
        }
    },

    createBookingSource: async (sourceData, tenantId) => {
        try {
            const response = await api.post(SETTINGS.BOOKING_SOURCES.BASE, sourceData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService createBookingSource error:', error);
            throw error;
        }
    },

    updateBookingSource: async (id, sourceData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.BOOKING_SOURCES.BY_ID(id), sourceData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateBookingSource error:', error);
            throw error;
        }
    },

    getPaymentMethods: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;

            const response = await api.get(SETTINGS.PAYMENT_METHODS.BASE, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            if (params.page !== undefined) return data;
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getPaymentMethods error:', error);
            throw error;
        }
    },
    updatePaymentMethod: async (id, paymentData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.PAYMENT_METHODS.BY_ID(id), paymentData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updatePaymentMethod error:', error);
            throw error;
        }
    },

    createPaymentMethod: async (paymentData, tenantId) => {
        try {
            const response = await api.post(SETTINGS.PAYMENT_METHODS.BASE, paymentData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService createPaymentMethod error:', error);
            throw error;
        }
    },

    deletePaymentMethod: async (id, tenantId) => {
        try {
            const response = await api.delete(SETTINGS.PAYMENT_METHODS.BY_ID(id), {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService deletePaymentMethod error:', error);
            throw error;
        }
    },

    updatePaymentMethod: async (id, methodData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.PAYMENT_METHODS.BY_ID(id), methodData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updatePaymentMethod error:', error);
            throw error;
        }
    },

    getEmailTemplates: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;

            const response = await api.get(SETTINGS.EMAIL_TEMPLATES.BASE, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            if (params.page !== undefined) return data;
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getEmailTemplates error:', error);
            throw error;
        }
    },

    updateEmailTemplate: async (id, templateData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.EMAIL_TEMPLATES.BY_ID(id), templateData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateEmailTemplate error:', error);
            throw error;
        }
    },

    sendTestEmail: async (id, recipientEmail, tenantId) => {
        try {
            const response = await api.post(SETTINGS.EMAIL_TEMPLATES.TEST(id), { recipientEmail }, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService sendTestEmail error:', error);
            throw error;
        }
    },

    getNotificationTemplates: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;

            const response = await api.get(SETTINGS.NOTIFICATIONS.TEMPLATES, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            if (params.page !== undefined) return data;
            return data.data || data;
        } catch (error) {
            console.error('SettingsService getNotificationTemplates error:', error);
            throw error;
        }
    },

    getNotificationTriggers: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;

            const response = await api.get(SETTINGS.NOTIFICATIONS.TRIGGERS, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService getNotificationTriggers error:', error);
            throw error;
        }
    },

    getNotificationChannels: async (tenantId, params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;

            const response = await api.get(SETTINGS.NOTIFICATIONS.CHANNELS, {
                params: axiosParams,
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService getNotificationChannels error:', error);
            throw error;
        }
    },

    updateNotificationTrigger: async (id, triggerData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.NOTIFICATIONS.TRIGGER_BY_ID(id), triggerData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateNotificationTrigger error:', error);
            throw error;
        }
    },


    updateNotificationTemplate: async (id, templateData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.NOTIFICATIONS.TEMPLATE_BY_ID(id), templateData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateNotificationTemplate error:', error);
            throw error;
        }
    },

    sendTestNotification: async (id, recipientEmail, tenantId) => {
        try {
            const response = await api.post(SETTINGS.NOTIFICATIONS.TEST_TEMPLATE(id), { recipientEmail }, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService sendTestNotification error:', error);
            throw error;
        }
    },
    
    updateNotificationChannel: async (type, channelData, tenantId) => {
        try {
            const response = await api.patch(SETTINGS.NOTIFICATIONS.CHANNEL_BY_TYPE(type), channelData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error('SettingsService updateNotificationChannel error:', error);
            throw error;
        }
    },
};
